// Blaivoré — sitewide ambient 3D corner accent (Three.js r128).
// The big floating-shape scene (blaivore-hero-3d.js) only mounts on the
// homepage hero. This is the lightweight sibling that brings a quiet bit
// of 3D presence to every OTHER page — collection, product, cart — so the
// interactive/3D feel reaches most of the store, not just the homepage.
// Small canvas, few shapes, gentle idle motion only. Skipped entirely on
// touch/narrow/reduced-motion, and on the homepage (already has the big
// scene). If WebGL isn't available or anything here throws, the page just
// renders exactly as before — pure visual add-on, no effect on layout or
// content, no effect on cart/checkout.

(function () {
  "use strict";

  function safe(label, fn) {
    try {
      fn();
    } catch (err) {
      if (window.console && console.warn) {
        console.warn("[blaivore-3d-ambient] " + label + " failed:", err);
      }
    }
  }

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var isTouch = window.matchMedia("(hover: none), (pointer: coarse)").matches;
  var isNarrow = window.innerWidth < 700;
  var hasHomepageScene = !!document.querySelector(".hero .hero__media-grid");

  if (reduceMotion || isTouch || isNarrow || hasHomepageScene) return;

  safe("ambient 3d corner scene", function () {
    if (!window.THREE) return;

    var host = document.createElement("div");
    host.className = "bx-ambient-3d";
    host.setAttribute("aria-hidden", "true");
    document.body.appendChild(host);

    var width = host.clientWidth || 320;
    var height = host.clientHeight || 320;
    if (!width || !height) return;

    var canvas = document.createElement("canvas");
    var renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas: canvas, alpha: true, antialias: true });
    } catch (e) {
      return; // WebGL unavailable — page renders exactly as before
    }
    if (!renderer) return;

    host.appendChild(canvas);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(width, height);

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(40, width / height, 0.1, 50);
    camera.position.z = 12;

    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    var key = new THREE.DirectionalLight(0xffffff, 1.15);
    key.position.set(4, 5, 6);
    scene.add(key);
    var rim = new THREE.DirectionalLight(0xffffff, 0.55);
    rim.position.set(-4, -2, 3);
    scene.add(rim);

    var geometries = [
      new THREE.IcosahedronGeometry(1, 0),
      new THREE.OctahedronGeometry(0.9, 0),
      new THREE.TorusGeometry(0.7, 0.22, 10, 22)
    ];
    // No pure-black entries — these sit over a near-black page background.
    var palette = [0x000000, 0x1a1a1a, 0x555555];

    var shapes = [];
    var COUNT = 5;
    for (var i = 0; i < COUNT; i++) {
      var geo = geometries[i % geometries.length];
      var mat = new THREE.MeshStandardMaterial({
        color: palette[i % palette.length],
        roughness: 0.4,
        metalness: 0.15,
        transparent: true,
        opacity: 0.85
      });
      var mesh = new THREE.Mesh(geo, mat);
      var scale = 0.32 + Math.random() * 0.5;
      mesh.scale.setScalar(scale);
      mesh.position.set(
        (Math.random() - 0.5) * 5.5,
        (Math.random() - 0.5) * 5.5,
        (Math.random() - 0.5) * 4
      );
      mesh.userData.spin = { x: (Math.random() - 0.5) * 0.006, y: (Math.random() - 0.5) * 0.008 };
      mesh.userData.bob = {
        speed: 0.3 + Math.random() * 0.4,
        offset: Math.random() * Math.PI * 2,
        amp: 0.2 + Math.random() * 0.3
      };
      mesh.userData.baseY = mesh.position.y;
      scene.add(mesh);
      shapes.push(mesh);
    }

    var running = true;
    var clock = new THREE.Clock();

    function animate() {
      if (!running) return;
      requestAnimationFrame(animate);
      var t = clock.getElapsedTime();

      shapes.forEach(function (mesh) {
        mesh.rotation.x += mesh.userData.spin.x;
        mesh.rotation.y += mesh.userData.spin.y;
        var b = mesh.userData.bob;
        mesh.position.y = mesh.userData.baseY + Math.sin(t * b.speed + b.offset) * b.amp;
      });

      renderer.render(scene, camera);
    }
    requestAnimationFrame(animate);

    // Pause the render loop when the tab isn't visible — this canvas has
    // no scroll-based visibility of its own since it's fixed to the
    // viewport, so page-visibility is the right signal to save battery.
    if ("visibilityState" in document) {
      document.addEventListener("visibilitychange", function () {
        var wasRunning = running;
        running = document.visibilityState === "visible";
        if (running && !wasRunning) requestAnimationFrame(animate);
      });
    }

    function onResize() {
      var w = host.clientWidth || 320, h = host.clientHeight || 320;
      if (!w || !h) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    }
    window.addEventListener("resize", onResize);
  });
})();
